import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Search } from "lucide-react";
import { Badge, Button, FormField, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { businessesApi } from "@/api/counterparties";
import { PartyPicker, type SelectedParty } from "@/components/parties/PartyPicker";
import { partyBasePath, type PartyType } from "@/types/parties";
import { useChangeCounterparty, usePatchDeal } from "@/hooks/deals/useDeals";
import type { DealDetailDto, PatchDealRequest } from "@/types/deals";

type AttachRole = "Borrower" | "Seller" | "Business";

interface Props {
  open: boolean;
  deal: DealDetailDto | null;
  role: AttachRole;
  /** When set, the modal is in "change" mode: the picked party is submitted
   *  via the audited change-counterparty endpoint carrying this reason
   *  instead of the plain PATCH-update attach path. */
  changeReason?: string | null;
  onClose: () => void;
  onAttached?: (deal: DealDetailDto) => void;
}

/**
 * Spec §5.5 — Intake tab counterparty attachment.
 *
 * Counterparty sunset: the Borrower / Seller arms select a Party (the
 * Borrower/Seller registries) via the shared PartyPicker typeahead and write
 * `borrowerPartyId` / `sellerPartyId` onto the Deal via PATCH /api/deals/{id}.
 * The Business arm still searches the Business store and writes `businessId`.
 * For the create-new branch the modal links out to the dedicated creation
 * page; on returning to the deal the user re-searches and attaches.
 */
export function AttachCounterpartyModal({
  open,
  deal,
  role,
  changeReason,
  onClose,
  onAttached,
}: Props) {
  const toast = useToast();
  const [selectedParty, setSelectedParty] = useState<SelectedParty | null>(null);
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);

  const patchDeal = usePatchDeal(deal?.id ?? "");
  const changeCounterparty = useChangeCounterparty(deal?.id ?? "");
  const isChangeMode = changeReason != null;
  const isBusiness = role === "Business";
  const partyType: PartyType = role === "Seller" ? "Seller" : "Borrower";

  useEffect(() => {
    if (open) {
      setSelectedParty(null);
      setSearch("");
      setError(null);
    }
  }, [open, role]);

  const businessesQuery = useQuery({
    queryKey: ["counterparties", "businesses", "search", search],
    queryFn: () => businessesApi.list({ q: search || undefined, pageSize: 25 }),
    enabled: open && isBusiness,
  });

  const businessHits = useMemo(
    () =>
      (businessesQuery.data?.data ?? []).map((b) => ({
        id: b.id,
        displayName: b.legalEntityName,
        subtitle: [b.naicsCode, b.hqState].filter(Boolean).join(" · ") || null,
      })),
    [businessesQuery.data],
  );

  if (!deal) return null;

  // Send ONLY the counterparty field being attached; PATCH merges it and
  // preserves everything else (null = leave unchanged, FIND-12).
  function buildPatchBody(pickedId: string): PatchDealRequest {
    if (role === "Borrower") return { borrowerPartyId: pickedId };
    if (role === "Seller") return { sellerPartyId: pickedId };
    return { businessId: pickedId };
  }

  async function attach(pickedId: string) {
    setError(null);
    try {
      const result = isChangeMode
        ? await changeCounterparty.mutateAsync({
            partyType: role,
            newPartyId: pickedId,
            reason: changeReason!,
          })
        : await patchDeal.mutateAsync(buildPatchBody(pickedId));
      toast.success(isChangeMode ? `${role} changed.` : `${role} attached.`);
      onAttached?.(result);
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Attach failed.",
      );
    }
  }

  const createNewPath = isBusiness
    ? "/counterparties/businesses/new"
    : `${partyBasePath(partyType)}/new`;
  const createNewLabel = isBusiness ? "business" : partyType.toLowerCase();

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`${isChangeMode ? "Change" : "Attach"} ${role}`}
      description={`${deal.dealNumber} — pick an existing record or create a new one.`}
      size="lg"
    >
      <div className="flex flex-col gap-space-3">
        {isBusiness ? (
          <>
            <FormField label="Search" htmlFor="attach-search">
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-space-2 top-1/2 -translate-y-1/2 text-gray-mid pointer-events-none"
                  aria-hidden
                />
                <Input
                  id="attach-search"
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search businesses by name…"
                  className="pl-space-5"
                  autoFocus
                />
              </div>
            </FormField>

            <div className="border border-border-light rounded-md overflow-hidden">
              {businessesQuery.isLoading ? (
                <div className="p-space-4 text-small text-gray-mid italic">Searching…</div>
              ) : businessHits.length === 0 ? (
                <div className="p-space-4 text-small text-gray-mid italic">
                  No matches. Try a broader search or create a new record.
                </div>
              ) : (
                <ul className="divide-y divide-border-light max-h-[320px] overflow-y-auto m-0 p-0 list-none">
                  {businessHits.map((h) => (
                    <li key={h.id} className="flex items-center justify-between gap-space-3 p-space-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-space-2">
                          <Badge status="info">Business</Badge>
                          <span className="font-semibold text-small text-onyx truncate">
                            {h.displayName}
                          </span>
                        </div>
                        {h.subtitle && (
                          <p className="text-caption text-gray-mid mt-space-1 m-0">{h.subtitle}</p>
                        )}
                      </div>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => attach(h.id)}
                        loading={patchDeal.isPending || changeCounterparty.isPending}
                      >
                        {isChangeMode ? "Select" : "Attach"}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        ) : (
          <>
            <PartyPicker
              type={partyType}
              value={selectedParty}
              onChange={setSelectedParty}
              label={`Search ${partyType.toLowerCase()} parties`}
            />
            <div className="flex justify-end">
              <Button
                variant="primary"
                size="sm"
                disabled={!selectedParty}
                onClick={() => selectedParty && attach(selectedParty.id)}
                loading={patchDeal.isPending || changeCounterparty.isPending}
              >
                {isChangeMode ? "Select" : "Attach"}
              </Button>
            </div>
          </>
        )}

        <div className="flex items-center justify-between text-caption text-gray-mid border-t border-border-light pt-space-3">
          <span>Don&apos;t see them?</span>
          <a
            href={createNewPath}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-space-1 text-gold-shadow hover:underline"
          >
            Create new {createNewLabel} <ExternalLink size={12} aria-hidden />
          </a>
        </div>

        {error && (
          <p className="text-small text-status-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
